<template>
  <section>
    <div class="progress">
      <div class="progress-container">
        <div class="progress-bar" :style="getProgressStyles"></div>
        <div class="goal-anchor" style="top: -20px;">{{ deliveryPrice }}₴</div>
        <!-- We don't do length - 1 below because we have initial anchor not included into tiers array, so we have to add it manually -->
        <div
          class="progress-anchors"
          v-for="(tier, index) in getProgressSettings.tiers"
          :key="index"
          :style="{
            left: (index === getProgressSettings.tiers.length - 1) ? 'calc(100% - 24px)' : `${((index + 1) / getProgressSettings.tiers.length) * 100}%`
          }"
        >
          <div class="goal-anchor">{{ tier.remaining | price('₴') }}</div>
        </div>
      </div>
    </div>
    <div class="delivery-price-info">
      <span>
        {{ getProgressSettings.progressStatus }}
      </span>
    </div>
  </section>
</template>

<script async>
export default {
  name: 'DeliveryCoverageProgress',
  props: {
    subtotal: {
      type: Number,
      required: true
    },
    deliveryPrice: {
      type: Number,
      required: true,
    },
    deliveryPriceCoverage: {
      type: Array,
      required: true
    }
  },
  computed: {
    getProgressSettings () {
      let tiers = this.deliveryPriceCoverage.sort((a, b) => a.from_cart_amount - b.from_cart_amount);
      let currentTierIdx = -1;

      if (tiers.length && tiers[0].coverage_amount >= this.deliveryPrice && tiers[0].from_cart_amount <= this.subtotal) {
        let finalTier = tiers[0];
        return {
          tiers: [{...finalTier, remaining: 0}],
          coverage: finalTier.coverage_amount,
          remaining: 0,
          freeDelivery: true,
          progressStatus: `${ this.$t('Free delivery') } 🎉`
        }
      }

      if (tiers.length === 1 && tiers[0].from_cart_amount <= this.subtotal) {
        let finalTier = tiers[0];
        let remaining = this.deliveryPrice - finalTier.coverage_amount > 0 ? this.deliveryPrice - finalTier.coverage_amount : 0;
        let freeDelivery = remaining <= 0;
        return {
          tiers: [{...finalTier, remaining}],
          coverage: finalTier.coverage_amount,
          remaining,
          freeDelivery,
          progressStatus: freeDelivery ? `${ this.$t('Free delivery') } 🎉` : `${this.$t('Cheaper delivery') } 🎉`
        }
      }

      tiers = tiers.map((tier, index) => {
        const reached = this.subtotal >= tier.from_cart_amount;
        currentTierIdx = reached ? index : currentTierIdx;

        return {
          ...tier,
          remaining: this.deliveryPrice - tier.coverage_amount,
          reached
        };
      });

      // Sometimes we might have more than one free tier. We want to get rid of all extra
      let freeIdx = tiers.findIndex(t => t.coverage_amount >= this.deliveryPrice);
      if (freeIdx >= 0) {
        tiers = tiers.slice(0, freeIdx + 1);
      }

      let lastTierIdx = tiers.length - 1;
      const coverage = currentTierIdx >= 0 ? tiers[currentTierIdx].coverage_amount : 0;
      const isLastTier = currentTierIdx === lastTierIdx;
      const nextTier = !isLastTier && currentTierIdx >= 0
        ? tiers[currentTierIdx + 1]
        : tiers[0];

      const remaining = nextTier
        ? Math.max(nextTier.from_cart_amount - this.subtotal, 0)
        : 0;
      const freeDelivery = currentTierIdx >= 0 && (coverage >= this.deliveryPrice);

      let progressStatus = null;
      if (isLastTier) {
        progressStatus = freeDelivery ? `${ this.$t('Free delivery') } 🎉` : `${this.$t('Cheaper delivery') } 🎉`
      }
      if (!isLastTier) {
        progressStatus = nextTier.coverage_amount < this.deliveryPrice
          ? `${this.$t('For cheaper delivery left ')} ${remaining.toFixed(2)} ${this.$t(' UAH')}`
          : `${this.$t('For free delivery left ')} ${remaining.toFixed(2)} ${this.$t(' UAH')}`;
      }

      return {
        tiers,
        coverage,
        remaining,
        freeDelivery,
        progressStatus
      };
    },
    getProgressStyles () {
      let width = this.getCurrentProgress
      let transition = 'width 100ms ease-in-out'
      let transitionDelay = '0.1s'
      if (width !== 100 && width !== 0) {
        transition = 'width 500ms ease-in-out'
        transitionDelay = '0.2s'
      }
      width += '%'
      return { width, transition, transitionDelay };
    },
    getCurrentProgress () {
      const thresholds = [
        0,
        ...(this.getProgressSettings.tiers || []).map(t => Number(t.from_cart_amount))
      ].sort((a, b) => a - b);

      const subtotal = this.subtotal ? this.subtotal : 0;
      const steps = thresholds.length - 1;

      if (steps <= 0) return 0;
      if (subtotal <= thresholds[0]) return 0;
      if (subtotal >= thresholds[thresholds.length - 1]) return 100;

      let completed = 0;
      let fraction = 0;

      for (let i = 0; i < steps; i++) {
        const start = thresholds[i];
        const end = thresholds[i + 1];

        if (subtotal >= end) {
          completed++;
        } else if (subtotal > start) {
          // Partially filled current step
          fraction = (subtotal - start) / (end - start);
          break;
        }
      }

      const totalProgress = (completed + fraction) / steps;
      return Math.round(totalProgress * 100);
    }
  }
}
</script>
<style lang="scss" scoped>
.progress {
  width: 90%;
  padding-top: 44px;
  margin: 0 auto;
  position: relative;
}

.progress-container {
  position: relative;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
}

.progress-bar {
  height: 100%;
  background-color: #ffa500;
  width: 0;
  border-radius: 3px;
}

.progress-anchors {
  position: absolute;
  top: -20px; /* move anchors above the bar */
  left: 0;
  right: 0;
  height: 30px;
}

.goal-anchor {
  position: absolute;
  transform: translateX(-50%);
  font-size: 14px;
  color: black;
  font-weight: bold;
  white-space: nowrap;
}
.delivery-price-info {
  padding-top: 8px;
  font-size: 16px;
  color: black;
  font-weight: bold;
  display: flex;
  justify-content: center;
  text-align: center;
}
</style>
